import React, {forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {
  Add,
  ArrowDropDown,
  ArrowDropUp,
  Dashboard,
  Delete,
  Edit,
  GetApp,
  Person,
  Settings,
  Source
} from "@mui/icons-material";
import {Box} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {useTranslation} from "react-i18next";
import {connect} from "react-redux";
import {compose} from "redux";
import {NODE_TYPE_DATAPORTAL, NODE_TYPE_MERGE, NODE_TYPE_SDMX} from "../../../constants/nodes.js";
import {useHasFeature} from "../../../hooks/useHasFeature.js";
import {NodeDataAccessibility, NodeVisibility} from "../../../model/IHubMinimalNode.d.ts";
import AlertDialog from "../../alert-dialog";
import CustomDialogTitle from "../../custom-dialog-title";
import MaterialTable from "../../material-table";
import ModulesPlaceholder from "../../modules-placeholder";
import {usePackageManager} from "../../package-manager";
import {PACKAGE_MANAGER_SOURCE_NODES_SETTINGS} from "../../package-manager/constants";
import SettingsDialog from "../../settings-dialog";
import AdvancedConfigurationForm from "./advanced-configuration-form";
import CacheSettingsForm from "./cache-setting-form";
import DashboardsSettingsForm from "./dashboards-settings-form";
import MergedNodeSettingsForm from "./merged-nodes-setting-form";
import NodeExportForm from "./node-export-form";
import NodeImportDialog from "./node-import-form";
import NodeSettingsForm from "./node-settings-form";
import PermissionsSettingsForm from "./permissions-settings-form";
import TemplatesSettingsForm from "./templates-settings-form";
import {LICENSE_FEATURES_SDMX} from "../../../state/app/contants.js";
import {
  clearNodesConfigNodes,
  closeNodesConfig,
  deleteNodesConfigNode,
  fetchNodesConfigNodes,
  sendNodesConfigNodesOrder,
  setNodesConfigMergedNodeDialogVisibility,
  setNodesConfigMergedNodeId,
  setNodesConfigNodeId,
  setNodesConfigNodeVisibility,
  submitNodeConfigsNodeDashboards
} from "../../../state/nodes-config/nodesConfigActions.js";
import {localizeI18nObj} from "../../../utils/i18n.js";
import {
  canCreateNode,
  canDeleteNodes,
  canDisplayCacheSettingForm,
  canDisplayPermissionsSettingsForm,
  canDisplayTemplatesSettingsForm,
  canEditNode,
  canManageNodeDashboards,
  canSortNodes,
  canViewNode
} from "../../../utils/permissions.js";
import "./style.css";

const buttonActionStyle = theme => ({
  ...theme.typography.button
});

const mapStateToProps = state => ({
  nodes: state.nodesConfig.nodes,
  isNodeVisible: state.nodesConfig.isNodeVisible,
  nodeId: state.nodesConfig.nodeId,
  isMergedNodeDialogVisible: state.nodesConfig.isMergedNodeDialogVisible,
  mergedNodeId: state.nodesConfig.mergedNodeId,
  user: state.user,
  language: state.app.language,
  languages: state.app.languages,
  moduleConfigs: state.moduleConfigs,
  tableActionFactories: state.moduleConfigs.tableActionFactories,
  maxNodes: state.app.license?.features?.MaxNodes
});

const mapDispatchToProps = dispatch => ({
  setNodeVisibility: visible => dispatch(setNodesConfigNodeVisibility(visible)),
  setNodeId: nodeId => dispatch(setNodesConfigNodeId(nodeId)),
  fetchConfig: () => dispatch(fetchNodesConfigNodes()),
  sendOrders: orderedIds => dispatch(sendNodesConfigNodesOrder(orderedIds)),
  deleteNode: nodeId => dispatch(deleteNodesConfigNode(nodeId)),
  clearConfig: () => dispatch(clearNodesConfigNodes()),
  onClose: () => dispatch(closeNodesConfig()),
  setMergedNodeId: nodeId => dispatch(setNodesConfigMergedNodeId(nodeId)),
  setMergedNodeDialogVisibility: visible => dispatch(setNodesConfigMergedNodeDialogVisibility(visible)),
  submitNodeDashboards: (nodeId, data) => dispatch(submitNodeConfigsNodeDashboards(nodeId, data))
});

const NodesSettingsForm = (
  {
    nodes,
    isNodeVisible,
    nodeId,
    setNodeVisibility,
    setNodeId,
    fetchConfig,
    sendOrders,
    deleteNode,
    clearConfig,
    user,
    onClose,
    language,
    languages,
    moduleConfigs,
    onNavigate,
    isMergedNodeDialogVisible,
    mergedNodeId,
    setMergedNodeId,
    setMergedNodeDialogVisibility,
    submitNodeDashboards,
    tableActionFactories,
    maxNodes
  },
  ref
) => {
  const isSdmxFeatureEnabled = useHasFeature(LICENSE_FEATURES_SDMX);
  const {openPackageManager} = usePackageManager();

  const [needConfig, setNeedConfig] = useState(true);
  const {t, i18n} = useTranslation();

  useEffect(() => {
    if (needConfig) {
      setNeedConfig(false);
      fetchConfig();
    }
  }, [nodes, needConfig, setNeedConfig, fetchConfig]);

  useImperativeHandle(ref, () => ({
    cancel(f) {
      clearConfig();
      onClose();
      f();
    }
  }));

  const [nodeDeleteFormNodeId, setNodeDeleteFormNodeId] = useState(null);
  const [cacheFormNodeId, setCacheFormNodeId] = useState(null);
  const [isCacheFormVisible, setCacheFormVisibility] = useState(false);
  const [templatesNodeId, setTemplatesNodeId] = useState(null);
  const [permissionsNodeId, setPermissionsNodeId] = useState(null);
  const [dashboardsNodeId, setDashboardsNodeId] = useState(null);
  const [isImportDialog, setImportDialog] = useState(false);
  const [exportNodeFormNodeId, setExportNodeFormNodeId] = useState(null);
  const [additionalSettingsNodeId, setAdditionalSettingsNodeId] = useState(null);

  const exportRef = useRef();
  const nodeFormRef = useRef();
  const cacheFormRef = useRef();
  const templatesRef = useRef();
  const permissionsRef = useRef();
  const dashboardsRef = useRef();
  const mergedNodeFormRef = useRef();

  const orderedNodes = (nodes || []).sort((a, b) => a.order - b.order);
  const orderedNodesIds = orderedNodes.map(({nodeId}) => nodeId);

  const isBelowNodeLimit = useMemo(
    () =>
      !maxNodes ||
      (nodes || []).filter(node => node.type !== NODE_TYPE_SDMX && node.type !== NODE_TYPE_MERGE).length < maxNodes,
    [maxNodes, nodes]
  );

  const moveUp = rowIndex =>
    sendOrders([
      ...orderedNodesIds.slice(0, rowIndex - 1),
      orderedNodesIds[rowIndex],
      orderedNodesIds[rowIndex - 1],
      ...orderedNodesIds.slice(rowIndex + 1)
    ]);

  const moveDown = rowIndex =>
    sendOrders([
      ...orderedNodesIds.slice(0, rowIndex),
      orderedNodesIds[rowIndex + 1],
      orderedNodesIds[rowIndex],
      ...orderedNodesIds.slice(rowIndex + 2)
    ]);

  const handleEditNode = (nodeId, type) => {
    if (type === NODE_TYPE_SDMX) {
      setMergedNodeDialogVisibility(false);
      setMergedNodeId(null);
      setNodeId(nodeId);
      setNodeVisibility(true);
    } else {
      setNodeId(null);
      setNodeVisibility(false);
      setMergedNodeDialogVisibility(true);
      setMergedNodeId(nodeId);
    }
    setCacheFormVisibility(false);
  };

  return (
    nodes && (
      <Fragment>
        <Box className="nodes-settings__table" sx={{height: "100%"}} id="nodes-table">
          <MaterialTable
            idKey="nodeId"
            data={orderedNodes}
            rightActions={[
              canCreateNode(user)
                ? {
                    id: "nodes-table__right-actions__import-node",
                    label: t("scenes.nodesSettings.importNode"),
                    startIcon: <Add />,
                    onClick: () => {
                      setImportDialog(true);
                    }
                  }
                : null,
              canCreateNode(user)
                ? {
                    id: "nodes-table__right-actions__create-node-dropdown",
                    label: t("scenes.nodesSettings.createNode"),
                    isDropdown: true,
                    items: [
                      isSdmxFeatureEnabled
                        ? {
                            id: "nodes-table__right-actions__create-node",
                            label: t("scenes.nodesSettings.createNode") + " SDMX",
                            startIcon: <Add />,
                            onClick: () => {
                              setNodeId(null);
                              setNodeVisibility(true);
                              setCacheFormVisibility(false);
                            }
                          }
                        : null,
                      isSdmxFeatureEnabled
                        ? {
                            id: "nodes-table__right-actions__create-merged-node",
                            label: t("scenes.nodesSettings.createMergedNode"),
                            startIcon: <Add />,
                            onClick: () => {
                              setMergedNodeId(null);
                              setMergedNodeDialogVisibility(true);
                              setCacheFormVisibility(false);
                            }
                          }
                        : null,
                      ...(tableActionFactories?.["add-node-config-menu"] || []).map(getAction =>
                        getAction(isBelowNodeLimit)
                      )
                    ]
                  }
                : null
            ]}
            columns={[
              {
                field: "code",
                title: t("scenes.nodesSettings.table.columns.nodeCode"),
                getClassName: () => "nodes-table__columns__nodeCode"
              },
              {
                field: "title",
                title: t("scenes.nodesSettings.table.columns.nodeName"),
                render: ({title}) => localizeI18nObj(title, language, languages),
                getClassName: () => "nodes-table__columns__nodeName"
              },
              {
                field: "type",
                title: t("scenes.nodesSettings.table.columns.nodeType"),
                render: ({type}) =>
                  i18n.exists(`commons.node.types.${type}`) ? t(`commons.node.types.${type}`) : type,
                getClassName: () => "nodes-table__columns__nodeType"
              },
              {
                field: "active",
                title: t("scenes.nodesSettings.table.columns.isNodeActive.title"),
                render: ({active}) =>
                  active
                    ? t("scenes.nodesSettings.table.columns.isNodeActive.values.true")
                    : t("scenes.nodesSettings.table.columns.isNodeActive.values.false"),
                getClassName: ({active}) =>
                  `nodes-table__columns__isNodeActive ${active ? "nodes-table__columns__isNodeActive--true" : "nodes-table__columns__isNodeActive--false"}`
              },
              {
                field: "visible",
                title: t("scenes.nodesSettings.table.columns.isNodeVisible.title"),
                render: ({visible}) =>
                  visible === NodeVisibility.Yes
                    ? t("scenes.nodesSettings.table.columns.isNodeVisible.values.true")
                    : t("scenes.nodesSettings.table.columns.isNodeVisible.values.false"),
                getClassName: ({visible}) =>
                  `nodes-table__columns__isNodeVisible ${visible ? "nodes-table__columns__isNodeVisible--true" : "nodes-table__columns__isNodeVisible--false"}`
              },
              {
                field: "default",
                title: t("scenes.nodesSettings.table.columns.isNodeDefault.title"),
                render: node =>
                  node.default
                    ? t("scenes.nodesSettings.table.columns.isNodeDefault.values.true")
                    : t("scenes.nodesSettings.table.columns.isNodeDefault.values.false"),
                getClassName: node =>
                  `nodes-table__columns__isNodeDefault ${node.default ? "nodes-table__columns__isNodeDefault--true" : "nodes-table__columns__isNodeDefault--false"}`
              }
            ]}
            actions={[
              (_row, index) =>
                canSortNodes(user)
                  ? {
                      className: "nodes-table__actions__node-order-move-up",
                      icon: <ArrowDropUp />,
                      tooltip: t("scenes.nodesSettings.table.actions.nodeOrderMoveUp"),
                      onClick: (_ev, _row, index) => moveUp(index),
                      disabled: index === 0
                    }
                  : null,
              (_row, index) =>
                canSortNodes(user)
                  ? {
                      className: "nodes-table__actions__node-order-move-down",
                      icon: <ArrowDropDown />,
                      tooltip: t("scenes.nodesSettings.table.actions.nodeOrderMoveDown"),
                      onClick: (_ev, _row, index) => moveDown(index),
                      disabled: index === nodes.length - 1
                    }
                  : null,
              ...(tableActionFactories?.["node-list"] || []).map(getAction => getAction({sx: buttonActionStyle})),
              ({nodeId, type}) =>
                canDisplayCacheSettingForm(user, nodeId)
                  ? {
                      className: "nodes-table__actions__node-dataflows-cache",
                      icon: (
                        <Box sx={buttonActionStyle}>
                          {t("scenes.nodesSettings.table.actions.nodeDataflowsCache.label")}
                        </Box>
                      ),
                      tooltip: t("scenes.nodesSettings.table.actions.nodeDataflowsCache.tooltip"),
                      onClick: () => {
                        setCacheFormNodeId(nodeId);
                        setNodeVisibility(false);
                        setCacheFormVisibility(true);
                      },
                      disabled: type === NODE_TYPE_MERGE
                    }
                  : null,
              ({nodeId}) =>
                canDisplayTemplatesSettingsForm(user, nodeId)
                  ? {
                      className: "nodes-table__actions__node-templates",
                      icon: (
                        <Box sx={buttonActionStyle}>{t("scenes.nodesSettings.table.actions.nodeTemplates.label")}</Box>
                      ),
                      tooltip: t("scenes.nodesSettings.table.actions.nodeTemplates.tooltip"),
                      onClick: () => setTemplatesNodeId(nodeId)
                    }
                  : null,
              ({nodeId}) =>
                canDisplayPermissionsSettingsForm(user)
                  ? {
                      className: "nodes-table__actions__node-permissions",
                      icon: <Person />,
                      tooltip: t("scenes.nodesSettings.table.actions.nodePermissions"),
                      onClick: () => setPermissionsNodeId(nodeId)
                    }
                  : null,
              ({nodeId}) =>
                moduleConfigs.modules.includes("dashboard") && canManageNodeDashboards(user, nodeId)
                  ? {
                      className: "nodes-table__actions__node-dashboards",
                      icon: <Dashboard />,
                      tooltip: t("scenes.nodesSettings.table.actions.nodeDashboards"),
                      onClick: () => setDashboardsNodeId(nodeId)
                    }
                  : null,
              ({nodeId, type}) =>
                canEditNode(user, nodeId) && (type === NODE_TYPE_SDMX || type === NODE_TYPE_MERGE)
                  ? {
                      className: "nodes-table__actions__edit-node",
                      icon: <Edit />,
                      tooltip: t("scenes.nodesSettings.table.actions.editNode"),
                      onClick: () => handleEditNode(nodeId, type)
                    }
                  : null,
              ...(tableActionFactories?.["node-list__edit-node"] || []).map(getAction =>
                getAction({sx: buttonActionStyle, user: user})
              ),
              ({type, nodeId}) =>
                canEditNode(user, nodeId)
                  ? {
                      className: "nodes-table__actions__advanced-configuration",
                      icon: <Settings />,
                      tooltip: t("scenes.nodesSettings.table.actions.advancedConfiguration"),
                      onClick: () => setAdditionalSettingsNodeId(nodeId),
                      disabled: type === NODE_TYPE_MERGE
                    }
                  : null,
              ({nodeId}) =>
                canDeleteNodes(user)
                  ? {
                      className: "nodes-table__actions__delete-node",
                      icon: <Delete />,
                      tooltip: t("scenes.nodesSettings.table.actions.deleteNode"),
                      onClick: () => setNodeDeleteFormNodeId(nodeId)
                    }
                  : null,
              ({nodeId, type}) =>
                canEditNode(user, nodeId)
                  ? {
                      className: "nodes-table__actions__export-node",
                      icon: <GetApp />,
                      tooltip: t("scenes.nodesSettings.table.actions.exportNode"),
                      onClick: () => setExportNodeFormNodeId(nodeId),
                      disabled: type === NODE_TYPE_MERGE
                    }
                  : null,
              ({nodeId, code, active, visible, dataAccess, type}) => ({
                className: "nodes-table__actions__packages",
                tooltip: t("scenes.nodesSettings.table.actions.packages"),
                icon: <Source />,
                onClick: () =>
                  openPackageManager({
                    nodeId: nodeId,
                    nodeCode: code,
                    readOnly: type !== NODE_TYPE_DATAPORTAL,
                    onNavigate: onNavigate,
                    source: PACKAGE_MANAGER_SOURCE_NODES_SETTINGS
                  }),
                disabled:
                  !active ||
                  (visible === NodeVisibility.Profiled &&
                    dataAccess === NodeDataAccessibility.Profiled &&
                    !canViewNode(user, nodeId))
              })
            ]}
          />
        </Box>

        <SettingsDialog
          title={
            nodeId === null ? t("scenes.nodesSettings.modals.createNode") : t("scenes.nodesSettings.modals.editNode")
          }
          open={isNodeVisible}
          onClose={() => {
            if (nodeFormRef.current) {
              nodeFormRef.current.cancel(() => {
                setNodeVisibility(false);
                setNodeId(null);
              });
            } else {
              setNodeVisibility(false);
              setNodeId(null);
            }
          }}
          onSubmit={() => {
            if (nodeFormRef.current) {
              nodeFormRef.current.submit(() => {
                // setNodeVisibility(false);
                // setNodeId(null);
              });
            } else {
              // setNodeVisibility(false);
              // setNodeId(null);
            }
          }}
        >
          <NodeSettingsForm ref={nodeFormRef} nodeId={nodeId} />
        </SettingsDialog>

        <SettingsDialog
          title={
            mergedNodeId === null
              ? t("scenes.nodesSettings.modals.createMergedNode")
              : t("scenes.nodesSettings.modals.editMergedNode")
          }
          open={isMergedNodeDialogVisible}
          onClose={() => {
            if (mergedNodeFormRef.current) {
              mergedNodeFormRef.current.cancel(() => {
                setMergedNodeDialogVisibility(false);
                setMergedNodeId(null);
              });
            } else {
              setMergedNodeDialogVisibility(false);
              setMergedNodeId(null);
            }
          }}
          onSubmit={() => {
            if (mergedNodeFormRef.current) {
              mergedNodeFormRef.current.submit(() => {});
            }
          }}
        >
          <MergedNodeSettingsForm ref={mergedNodeFormRef} nodeId={mergedNodeId} />
        </SettingsDialog>

        {isImportDialog && (
          <NodeImportDialog
            open={isImportDialog}
            onClose={() => setImportDialog(false)}
            nodes={nodes}
            isBelowNodeLimit={isBelowNodeLimit}
          />
        )}

        <SettingsDialog
          id="node-settings-form__cache-dialog"
          title={t("scenes.nodesSettings.modals.nodeCache", {
            nodeCode: nodes.find(node => node.nodeId === cacheFormNodeId)?.code
          })}
          open={isCacheFormVisible}
          onClose={() => {
            if (cacheFormRef.current) {
              cacheFormRef.current.cancel(() => {
                setCacheFormVisibility(false);
                setCacheFormNodeId(null);
              });
            } else {
              setCacheFormVisibility(false);
              setCacheFormNodeId(null);
            }
          }}
          onSubmit={() => {
            if (cacheFormRef.current) {
              cacheFormRef.current.submit(() => {
                setCacheFormVisibility(false);
                setCacheFormNodeId(null);
              });
            } else {
              setCacheFormVisibility(false);
              setCacheFormNodeId(null);
            }
          }}
        >
          <CacheSettingsForm ref={cacheFormRef} nodeId={cacheFormNodeId} />
        </SettingsDialog>

        <SettingsDialog
          id={"node-settings-form__templates-dialog"}
          title={t("scenes.nodesSettings.modals.nodeTemplates", {
            nodeCode: nodes.find(node => node.nodeId === templatesNodeId)?.code
          })}
          open={templatesNodeId !== null}
          onClose={() => {
            if (templatesRef.current) {
              templatesRef.current.cancel(() => {
                setTemplatesNodeId(null);
              });
            } else {
              setTemplatesNodeId(null);
            }
          }}
        >
          <TemplatesSettingsForm
            ref={templatesRef}
            nodeId={templatesNodeId}
            nodes={nodes}
            onNavigate={() => {
              if (templatesRef.current) {
                templatesRef.current.cancel(() => {
                  setTemplatesNodeId(null);
                  if (onNavigate) onNavigate();
                });
              } else {
                setTemplatesNodeId(null);
                if (onNavigate) onNavigate();
              }
            }}
          />
        </SettingsDialog>

        <SettingsDialog
          title={t("scenes.nodesSettings.modals.nodePermissions", {
            nodeCode: nodes.find(node => node.nodeId === permissionsNodeId)?.code
          })}
          open={permissionsNodeId !== null}
          onClose={() => {
            if (permissionsRef.current) {
              permissionsRef.current.cancel(() => {
                setPermissionsNodeId(null);
              });
            } else {
              setPermissionsNodeId(null);
            }
          }}
          onSubmit={() => {
            if (permissionsRef.current) {
              permissionsRef.current.submit(() => {
                setPermissionsNodeId(null);
              });
            } else {
              setPermissionsNodeId(null);
            }
          }}
        >
          <PermissionsSettingsForm ref={permissionsRef} nodeId={permissionsNodeId} />
        </SettingsDialog>

        <SettingsDialog
          title={t("scenes.nodesSettings.modals.nodeDashboards", {
            nodeCode: nodes.find(node => node.nodeId === dashboardsNodeId)?.code
          })}
          open={moduleConfigs.modules.includes("dashboard") && dashboardsNodeId !== null}
          onClose={() => {
            if (dashboardsRef.current) {
              dashboardsRef.current.destroy(() => {
                setDashboardsNodeId(null);
              });
            } else {
              setDashboardsNodeId(null);
            }
          }}
          onSubmit={() => {
            if (dashboardsRef.current) {
              dashboardsRef.current.submit(data => {
                submitNodeDashboards(dashboardsNodeId, data);
                setDashboardsNodeId(null);
              });
            }
          }}
        >
          <DashboardsSettingsForm ref={dashboardsRef} nodeId={dashboardsNodeId} />
        </SettingsDialog>

        {exportNodeFormNodeId && (
          <NodeExportForm
            ref={exportRef}
            nodeCode={nodes.find(node => node.nodeId === exportNodeFormNodeId)?.code}
            onClose={() => {
              if (exportRef.current) {
                exportRef.current.cancel(() => {
                  setExportNodeFormNodeId(null);
                });
              } else {
                setExportNodeFormNodeId(null);
              }
            }}
          />
        )}

        {additionalSettingsNodeId && (
          <AdvancedConfigurationForm
            nodeId={additionalSettingsNodeId}
            onClose={() => setAdditionalSettingsNodeId(null)}
          />
        )}

        <AlertDialog
          open={nodeDeleteFormNodeId !== null}
          onClose={() => {
            setNodeDeleteFormNodeId(null);
            setCacheFormNodeId(null);
          }}
        >
          <CustomDialogTitle
            onClose={() => {
              setNodeDeleteFormNodeId(null);
              setCacheFormNodeId(null);
            }}
          >
            {t("scenes.nodesSettings.modals.nodeDelete.title")}
          </CustomDialogTitle>
          <DialogContent>{t("scenes.nodesSettings.modals.nodeDelete.content")}</DialogContent>
          <DialogActions>
            <Button
              id="nodesSettings_modals_nodeDelete_cancel_btn"
              autoFocus
              onClick={() => {
                setNodeDeleteFormNodeId(null);
                setCacheFormNodeId(null);
              }}
            >
              {t("commons.confirm.cancel")}
            </Button>
            <Button
              id="nodesSettings_modals_nodeDelete_confirm_btn"
              onClick={() => {
                deleteNode(nodeDeleteFormNodeId);
                setNodeDeleteFormNodeId(null);
                setCacheFormNodeId(null);
              }}
            >
              {t("commons.confirm.confirm")}
            </Button>
          </DialogActions>
        </AlertDialog>

        <ModulesPlaceholder id="node-settings-actions-dialog" />
      </Fragment>
    )
  );
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true}),
  forwardRef
)(NodesSettingsForm);
